import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';
import { fmtDate } from '../lib/fmt';

type SI = { id: string; si_number: string | null; status: string; currency: string; total: number; amount_paid: number; balance: number; invoice_date: string; tax_total: number; suppliers: { name: string } | null; supplier_id: string };
type Supplier = { id: string; name: string };
type NewLine = { description: string; quantity: number; unit_price: number };
const TONE: Record<string, 'open' | 'inprogress' | 'onhold' | 'done' | 'overdue'> = { draft: 'open', submitted: 'inprogress', approved: 'onhold', paid: 'done', void: 'overdue' };

export function Payables() {
  const { t, i18n } = useTranslation();
  const { id: orgId, currency } = useOrg();
  const loc = i18n.language;
  const [rows, setRows] = useState<SI[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [creating, setCreating] = useState(false);
  const [supplierId, setSupplierId] = useState('');
  const [tax, setTax] = useState(0);
  const [lines, setLines] = useState<NewLine[]>([{ description: '', quantity: 1, unit_price: 0 }]);
  const [payFor, setPayFor] = useState<SI | null>(null);
  const [payAmt, setPayAmt] = useState(0);
  const money = (n: number, c?: string) => formatCurrency(n, c || currency, loc);

  const load = useCallback(async () => {
    const [si, sup] = await Promise.all([
      supabase.from('supplier_invoices').select('id,si_number,status,currency,total,amount_paid,balance,invoice_date,tax_total,supplier_id,suppliers:suppliers(name)').is('deleted_at', null).order('invoice_date', { ascending: false }),
      supabase.from('suppliers').select('id,name').is('deleted_at', null).order('name'),
    ]);
    setRows((si.data as any) ?? []); setSuppliers((sup.data as any) ?? []);
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function create() {
    if (!supplierId) return;
    const si = (await supabase.from('supplier_invoices').insert({ organization_id: orgId, supplier_id: supplierId, currency, tax_total: tax, status: 'draft' }).select('id').single()).data as any;
    if (si) {
      const payload = lines.filter((l) => l.description.trim()).map((l) => ({ organization_id: orgId, supplier_invoice_id: si.id, description: l.description, quantity: l.quantity, unit_price: l.unit_price }));
      if (payload.length) await supabase.from('supplier_invoice_lines').insert(payload);
    }
    setCreating(false); setSupplierId(''); setTax(0); setLines([{ description: '', quantity: 1, unit_price: 0 }]); await load();
  }
  const act = async (fn: string, id: string) => { await supabase.rpc(fn, { p_si: id }); await load(); };
  async function pay() {
    if (!payFor || !payAmt) return;
    await supabase.from('supplier_payments').insert({ organization_id: orgId, supplier_invoice_id: payFor.id, amount: payAmt, currency: payFor.currency });
    setPayFor(null); setPayAmt(0); await load();
  }

  return (
    <Layout header={<Topbar actions={<Button onClick={() => setCreating((v) => !v)}>{t('ap.newInvoice')}</Button>} />}>
      <div className="px-7 py-6">
        <h1 className="mb-1 text-xl font-semibold">{t('nav.payables')}</h1>
        <p className="mb-5 text-sm text-muted">{t('ap.subtitle')}</p>

        {creating && (
          <Card className="mb-5 space-y-3 p-4">
            <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className="w-full rounded-md border border-border bg-card px-2.5 py-2 text-sm">
              <option value="">{t('ap.selectSupplier')}</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {lines.map((l, idx) => (
              <div key={idx} className="flex gap-2">
                <input value={l.description} onChange={(e) => setLines((p) => p.map((x, i) => (i === idx ? { ...x, description: e.target.value } : x)))} placeholder={t('ap.description')} className="flex-1 rounded-md border border-border bg-card px-2.5 py-2 text-sm" />
                <input type="number" value={l.quantity} onChange={(e) => setLines((p) => p.map((x, i) => (i === idx ? { ...x, quantity: Number(e.target.value) } : x)))} className="w-20 rounded-md border border-border bg-card px-2.5 py-2 text-sm" />
                <input type="number" value={l.unit_price} onChange={(e) => setLines((p) => p.map((x, i) => (i === idx ? { ...x, unit_price: Number(e.target.value) } : x)))} placeholder={t('ap.unitPrice')} className="w-28 rounded-md border border-border bg-card px-2.5 py-2 text-sm" />
              </div>
            ))}
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => setLines((p) => [...p, { description: '', quantity: 1, unit_price: 0 }])}>+ {t('ap.addLine')}</Button>
              <label className="text-sm text-muted">{t('ap.tax')}</label>
              <input type="number" value={tax} onChange={(e) => setTax(Number(e.target.value))} className="w-28 rounded-md border border-border bg-card px-2.5 py-2 text-sm" />
              <div className="flex-1" />
              <Button onClick={create}>{t('common.save')}</Button>
            </div>
          </Card>
        )}

        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted">
                <th className="px-4 py-2">{t('ap.number')}</th><th className="px-4 py-2">{t('ap.supplier')}</th><th className="px-4 py-2">{t('ap.date')}</th>
                <th className="px-4 py-2 text-right">{t('ap.total')}</th><th className="px-4 py-2 text-right">{t('ap.balance')}</th><th className="px-4 py-2">{t('ap.status')}</th><th className="px-4 py-2"></th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-divider">
                    <td className="px-4 py-2 font-mono text-xs">{r.si_number ?? '—'}</td>
                    <td className="px-4 py-2">{r.suppliers?.name}</td>
                    <td className="px-4 py-2 text-xs text-muted">{fmtDate(r.invoice_date, loc)}</td>
                    <td className="px-4 py-2 text-right font-mono">{money(r.total, r.currency)}</td>
                    <td className="px-4 py-2 text-right font-mono">{money(r.balance, r.currency)}</td>
                    <td className="px-4 py-2"><StatusPill tone={TONE[r.status] ?? 'open'}>{t('ap.st_' + r.status)}</StatusPill></td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        {r.status === 'draft' && <Button variant="ghost" onClick={() => act('submit_supplier_invoice', r.id)}>{t('ap.submit')}</Button>}
                        {(r.status === 'draft' || r.status === 'submitted') && <Button variant="ghost" onClick={() => act('approve_supplier_invoice', r.id)}>{t('ap.approve')}</Button>}
                        {(r.status === 'approved') && r.balance > 0 && <Button variant="ghost" onClick={() => { setPayFor(r); setPayAmt(r.balance); }}>{t('ap.pay')}</Button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted">{t('common.noData')}</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {payFor && (
        <div className="fixed inset-0 z-40 grid place-items-center bg-black/30" onClick={() => setPayFor(null)}>
          <Card className="w-[360px] space-y-3 p-5" onClick={(e: any) => e.stopPropagation()}>
            <h2 className="text-sm font-semibold">{t('ap.recordPayment')} · {payFor.si_number}</h2>
            <input type="number" value={payAmt} onChange={(e) => setPayAmt(Number(e.target.value))} className="w-full rounded-md border border-border bg-card px-2.5 py-2 text-sm" />
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setPayFor(null)}>{t('common.cancel')}</Button><Button onClick={pay}>{t('ap.pay')}</Button></div>
          </Card>
        </div>
      )}
    </Layout>
  );
}
